import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Checkbox } from 'app/design/primitives/checkbox';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useState } from 'react';
import { Pressable, View } from 'react-native';
import type { useAllAddresses } from 'app/network/address';
import type { Address } from 'app/types/address';
import type { useAllGroups } from 'app/network/groups';
import type { Group } from 'app/types/group';
import { IconAdjustments, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

export interface FiltersProps {
  addresses?: ReturnType<typeof useAllAddresses>['data'];
  addressesState: ReturnType<typeof useState<Address['id'][] | undefined>>;
  groups?: ReturnType<typeof useAllGroups>['data'];
  groupsState: ReturnType<typeof useState<Group['id'][] | undefined>>;
}

export function Filters({ addresses, addressesState, groups, groupsState }: FiltersProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAddresses, setSelectedAddresses] = addressesState;
  const [checkedAddresses, setCheckedAddresses] = useState<string[]>(selectedAddresses ?? []);
  const [selectedGroups, setSelectedGroups] = groupsState;
  const [checkedGroups, setCheckedGroups] = useState<string[]>(selectedGroups ?? []);
  const { t } = useScopedTranslation();
  const checkedAddressIds = new Set(checkedAddresses);
  const checkedGroupIds = new Set(checkedGroups);

  const toggleCheckbox = (id: string, isAddress: boolean) => {
    if (isAddress) {
      if (checkedAddresses.includes(id)) {
        setCheckedAddresses(checkedAddresses.filter((aId) => aId !== id));
      } else {
        setCheckedAddresses([...checkedAddresses, id]);
      }
    } else {
      if (checkedGroups.includes(id)) {
        setCheckedGroups(checkedGroups.filter((gId) => gId !== id));
      } else {
        setCheckedGroups([...checkedGroups, id]);
      }
    }
  };

  const showFiltersModal = () => {
    setCheckedAddresses(selectedAddresses ?? []);
    setCheckedGroups(selectedGroups ?? []);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setShowFilters(false);
    if (checkedGroups.length === 0 && checkedAddresses.length === 0) {
      setSelectedAddresses(addresses?.map((a) => a.id));
      setSelectedGroups(groups?.map((g) => g.id));
      return;
    }
    setSelectedAddresses(checkedAddresses);
    setSelectedGroups(checkedGroups);
  };

  return (
    <>
      <Button onPress={showFiltersModal} style={{ padding: 0, margin: 0 }}>
        <IconAdjustments strokeWidth={ICON_STROKE_WIDTH} size={30} />
      </Button>

      <Modal onClose={() => setShowFilters(false)} open={showFilters} transparent>
        <Modal.Card gap="spacing.general.0">
          <Row justifyContent="flex-end">
            <Pressable onPress={() => setShowFilters(false)}>
              <IconX strokeWidth={ICON_STROKE_WIDTH} size={20} />
            </Pressable>
          </Row>
          <Modal.Title>{t('filter.title')}</Modal.Title>
          <Column gap="spacing.general.x4" marginHorizontal="spacing.general.x4" marginVertical="spacing.general.x8">
            {addresses?.map((address) => (
              <Pressable key={address.id} onPress={() => toggleCheckbox(address.id, true)}>
                <Row alignItems="center">
                  <Checkbox
                    checked={checkedAddressIds.has(address.id)}
                    color="surface.brand.default"
                    onPress={() => toggleCheckbox(address.id, true)}
                  />

                  <Text
                    color="text.secondary"
                    flex={1}
                    fontWeight="700"
                    marginStart="spacing.general.x4"
                    variant="bodyBase"
                  >
                    {t('clubName', {
                      ns: 'my-neighbourhood',
                      clubName: address.name ?? t('neighbourhoodClubName', { ns: 'my-clubs' }),
                    })}
                  </Text>
                </Row>
              </Pressable>
            ))}
            {groups?.map((group) => (
              <Pressable key={group.id} onPress={() => toggleCheckbox(group.id, false)}>
                <Row alignItems="center">
                  <Checkbox
                    checked={checkedGroupIds.has(group.id)}
                    color="surface.brand.default"
                    onPress={() => toggleCheckbox(group.id, false)}
                  />

                  <Text
                    color="text.secondary"
                    flex={1}
                    fontWeight="700"
                    marginStart="spacing.general.x4"
                    variant="bodyBase"
                  >
                    {`Club ${group.name}`}
                  </Text>
                </Row>
              </Pressable>
            ))}
          </Column>
          <View>
            <Button
              marginBottom="spacing.general.x2"
              marginHorizontal="spacing.general.x4"
              onPress={applyFilters}
              variant="primary"
            >
              {t('filter.apply')}
            </Button>
          </View>
        </Modal.Card>
      </Modal>
    </>
  );
}
